/**
 * Papierkorb und Rueckgaengig (F-45) - der Kern.
 *
 * Mit der Datenbank als Parameter (Muster wie `db/sync/konflikte.ts`), damit
 * Tests mit zwei Geraeten arbeiten koennen; die App nutzt die Huellen in
 * `db/repositories/papierkorb.ts`.
 *
 * - `inDenPapierkorb`: fuehrt eine Loeschaktion in EINER Transaktion aus und
 *   legt alle dabei aufgezeichneten Aenderungen als einen Papierkorb-Eintrag
 *   ab. Verschachtelte Aufrufe (z. B. deleteExam -> deleteEvent) erzeugen
 *   keinen zweiten Eintrag.
 * - `rueckgaengigMachbar`: dasselbe fuer sonstige Aktionen (Abhaken,
 *   Umwandeln), aber nur im Speicher - als Meldung an `db.aufzeichnung`.
 * - `wiederherstellen` / `zuruecknehmen`: spielen die Aenderungen ueber ganz
 *   normale Schreibvorgaenge zurueck. Sie bekommen so neue Sync-Uhren und
 *   tauchen auf jedem Geraet wieder auf.
 *
 * Die Huellen gehoeren INS Repository, nie um eine UI-Abfrage herum: Eine
 * offene IndexedDB-Transaktion schliesst sich, sobald auf etwas anderes als
 * die Datenbank gewartet wird.
 */
import Dexie, { type Table } from 'dexie';

import type { OlePrivatDB } from '@/db/schema';
import { applyLinkRule } from '@/db/repositories/links';
import { aufzeichnungStarten, laufendeAufzeichnung } from '@/db/papierkorb/aufzeichnung';
import { alsDiff, ohneFelder, verdichten, zaehlen, zerlegen, zuGross, type Stueck } from '@/domain/papierkorb/verdichten';
import { benoetigteSchluessel, planen, schluesselVon, verweiseBereinigen, verweisZiele } from '@/domain/papierkorb/rueckspielen';
import { ablaufGrenze, vollstaendig } from '@/domain/papierkorb/ablauf';
import {
  ANHAENGE, type Aenderung, type AktionsArt, type AktionsMeldung, type Hinweis,
  type PapierkorbDatei, type PapierkorbEintrag, type PapierkorbZeile,
} from '@/domain/papierkorb/typen';
import { eintragTitel } from '@/domain/sync/typen';
import type { Daten } from '@/domain/sync/zusammenfuehren';
import { toBytes } from '@/lib/backup/attachments';
import { META } from '@/lib/sync/abgleich';
import { newId } from '@/lib/utils/id';

export interface AktionsBeschreibung {
  art: AktionsArt;
  /** Tabelle der Hauptzeile(n). */
  tabelle: string;
  /** Die Hauptzeilen - mehrere bei einer Mehrfachauswahl. */
  zeileIds: string[];
  /** Ohne Angabe: Titel der ersten Hauptzeile. */
  titel?: string;
}

export interface WiederherstellErgebnis {
  titel: string;
  /** Zahl der zurueckgeschriebenen oder geloeschten Zeilen. */
  zurueck: number;
  hinweise: Hinweis[];
  /** Zahl der Aenderungen, die im Papierkorb bleiben - 0: Eintrag ist weg. */
  rest: number;
}

/** Laeuft schon eine Transaktion dieser Datenbank, aber ohne Aufzeichnung? */
function inFremderTransaktion(db: OlePrivatDB, b: AktionsBeschreibung): boolean {
  const tx = Dexie.currentTransaction;
  if (!tx || tx.db !== db) return false;
  // Eine eigene Transaktion ueber alle Tabellen ginge hier nicht ('not part of
  // transaction'). Die Aktion laeuft trotzdem - nur ohne Papierkorb.
  console.warn(`Papierkorb: ${b.art} ${b.tabelle} laeuft in einer fremden Transaktion und wird nicht aufgezeichnet.`);
  return true;
}

function titelAus(aenderungen: readonly Aenderung[], b: AktionsBeschreibung): string {
  if (b.titel !== undefined) return b.titel;
  const a = aenderungen.find((x) => x.t === b.tabelle && x.id === b.zeileIds[0]);
  return eintragTitel(a?.vorher ?? a?.nachher);
}

/** Die Meldung darf eine erledigte Aktion nie als fehlgeschlagen erscheinen lassen. */
function melden(db: OlePrivatDB, m: AktionsMeldung): void {
  try {
    db.aufzeichnung.beiAktion?.(m);
  } catch (err) {
    console.error('Rückgängig-Meldung:', err);
  }
}

/**
 * Anhang-Bytes gehoeren nie in synchronisierte Zeilen (Server-Limit, und
 * JSON macht aus Bytes ein riesiges Objekt). Bei einem geloeschten Anhang
 * kommen sie in `papierkorbDateien`; erkannt am Schluessel `bytes`, nicht an
 * `instanceof` (siehe lib/backup/attachments.ts).
 */
function bytesAbtrennen(a: Aenderung, eintragId: string, dateien: PapierkorbDatei[]): Aenderung {
  const out: Aenderung = { ...a };
  if (a.vorher && 'bytes' in a.vorher) {
    out.vorher = ohneFelder(a.vorher, ['bytes']);
    if (!a.nachher && a.vorher.bytes != null) {
      dateien.push({ id: a.id, eintragId, bytes: toBytes(a.vorher.bytes) });
      out.datei = 1;
    }
  }
  if (a.nachher && 'bytes' in a.nachher) out.nachher = ohneFelder(a.nachher, ['bytes']);
  return out;
}

const zeilenAus = (eintragId: string, stand: string, geloeschtAm: string, stuecke: Stueck[]): PapierkorbZeile[] =>
  stuecke.map((s, nr) => ({ id: newId(), eintragId, nr, stand, geloeschtAm, schluessel: s.schluessel, aenderungen: s.aenderungen }));

/**
 * Kopf, Zeilen und Dateien schreiben. `undefined`: Ein Stueck waere fuer den
 * Sync-Server zu gross - dann bleibt die Aktion ohne Papierkorb-Eintrag.
 */
async function eintragSchreiben(db: OlePrivatDB, b: AktionsBeschreibung, verdichtet: Aenderung[]): Promise<AktionsMeldung | undefined> {
  const eintragId = newId();
  const stand = newId();
  const jetzt = new Date().toISOString();
  const titel = titelAus(verdichtet, b);
  const dateien: PapierkorbDatei[] = [];
  const aenderungen = verdichtet.map((a) => {
    const d = alsDiff(a);
    return d.t === ANHAENGE ? bytesAbtrennen(d, eintragId, dateien) : d;
  });
  const stuecke = zerlegen(aenderungen);
  if (zuGross(stuecke)) {
    console.warn(`Papierkorb: ${b.art} ${b.tabelle} ist zu gross für den Abgleich und kommt nicht in den Papierkorb.`);
    return undefined;
  }
  const konfig = (await db.meta.get(META.konfig))?.value as { geraetName?: string } | undefined;
  const kopf: PapierkorbEintrag = {
    id: eintragId, geloeschtAm: jetzt, art: b.art, tabelle: b.tabelle, zeileIds: [...b.zeileIds], titel,
    zaehler: zaehlen(aenderungen), zeilen: stuecke.length, stand, updatedAt: jetzt,
  };
  if (konfig?.geraetName) kopf.geraet = konfig.geraetName;
  await db.papierkorb.add(kopf);
  await db.papierkorbZeilen.bulkAdd(zeilenAus(eintragId, stand, jetzt, stuecke));
  if (dateien.length) await db.papierkorbDateien.bulkPut(dateien);
  return { art: b.art, tabelle: b.tabelle, titel, anzahl: b.zeileIds.length, eintragId };
}

/**
 * Eine Loeschaktion ausfuehren und als EINEN Papierkorb-Eintrag ablegen.
 * Erst nach dem Commit geht die Meldung an `db.aufzeichnung.beiAktion`.
 * Ohne Aenderungen entsteht weder Eintrag noch Meldung.
 */
export async function inDenPapierkorb<T>(db: OlePrivatDB, b: AktionsBeschreibung, fn: () => Promise<T>): Promise<T> {
  if (laufendeAufzeichnung(db) || inFremderTransaktion(db, b)) return fn();
  const aus: { meldung?: AktionsMeldung } = {};
  const ergebnis = await db.transaction('rw', db.tables, async (tx) => {
    const roh = aufzeichnungStarten(tx);
    const r = await fn();
    const verdichtet = verdichten(roh);
    if (verdichtet.length) {
      const meldung = await eintragSchreiben(db, b, verdichtet);
      if (meldung) aus.meldung = meldung;
    }
    return r;
  });
  if (aus.meldung) melden(db, aus.meldung);
  return ergebnis;
}

/**
 * Eine sonstige Aktion (Abhaken, Umwandeln ...) rueckgaengig machbar
 * ausfuehren: dieselbe Aufzeichnung, aber nur im Speicher. `tabellen` grenzt
 * die Transaktion ein (z. B. Aufgaben und Termine beim Abhaken).
 */
export async function rueckgaengigMachbar<T>(db: OlePrivatDB, b: AktionsBeschreibung, fn: () => Promise<T>, tabellen?: Table[]): Promise<T> {
  if (laufendeAufzeichnung(db) || inFremderTransaktion(db, b)) return fn();
  const aus: { meldung?: AktionsMeldung } = {};
  const ergebnis = await db.transaction('rw', tabellen ?? db.tables, async (tx) => {
    const roh = aufzeichnungStarten(tx);
    const r = await fn();
    const verdichtet = verdichten(roh);
    if (verdichtet.length) {
      aus.meldung = { art: b.art, tabelle: b.tabelle, titel: titelAus(verdichtet, b), anzahl: b.zeileIds.length, aenderungen: verdichtet.map(alsDiff) };
    }
    return r;
  });
  if (aus.meldung) melden(db, aus.meldung);
  return ergebnis;
}

/** Zeilen je Tabelle per `bulkGet` lesen; unbekannte Tabellen werden uebersprungen. */
async function lesen(db: OlePrivatDB, schluessel: readonly { t: string; id: string }[]): Promise<Map<string, Daten>> {
  const vorhanden = new Set(db.tables.map((t) => t.name));
  const nachTabelle = new Map<string, string[]>();
  for (const { t, id } of schluessel) if (vorhanden.has(t)) nachTabelle.set(t, [...(nachTabelle.get(t) ?? []), id]);
  const out = new Map<string, Daten>();
  for (const [t, ids] of nachTabelle) {
    const zeilen = (await db.table(t).bulkGet(ids)) as (Daten | undefined)[];
    zeilen.forEach((z, i) => { if (z) out.set(schluesselVon(t, ids[i]!), z); });
  }
  return out;
}

/**
 * Titel einer fehlenden Zeile, wenn sie geloescht im Papierkorb liegt - fuer
 * den Hinweis „„Milch“ braucht „Einkauf“ ...“ statt „braucht etwas“.
 */
async function titelImPapierkorb(db: OlePrivatDB, t: string, id: string): Promise<string | undefined> {
  const zeilen = await db.papierkorbZeilen.where('schluessel').equals(schluesselVon(t, id)).toArray();
  for (const z of zeilen) {
    const a = z.aenderungen.find((x) => x.t === t && x.id === id && x.vorher && !x.nachher);
    const titel = eintragTitel(a?.vorher);
    if (titel) return titel;
  }
  return undefined;
}

async function hinweiseBenennen(db: OlePrivatDB, hinweise: Hinweis[]): Promise<void> {
  for (const h of hinweise) {
    if (!h.eltern) continue;
    const titel = await titelImPapierkorb(db, h.eltern.t, h.eltern.id);
    if (titel) h.eltern = { ...h.eltern, titel };
  }
}

/**
 * Plan aufstellen und schreiben: Zeilen zurueck, angelegte weg, Verweise ins
 * Leere bereinigen (in einem Schreibvorgang je Zeile), dann die
 * Verknuepfungsregel (F-11). Laeuft in der Transaktion des Aufrufers.
 *
 * `ganzOderGar` (Rueckgaengig ohne Papierkorb): Kann etwas nicht zurueck,
 * wird gar nichts geschrieben - sonst verschwaende z. B. die umgewandelte
 * Aufgabe, waehrend der Listeneintrag nicht zurueckkommt, und es gaebe
 * keinen Papierkorb, in dem der Rest bleiben koennte.
 */
async function zurueckspielen(db: OlePrivatDB, aenderungen: readonly Aenderung[], dateien: readonly PapierkorbDatei[], jetzt: string, ganzOderGar = false) {
  const bytes = new Map(dateien.map((d) => [d.id, d.bytes]));
  const aktuell = await lesen(db, benoetigteSchluessel(aenderungen));
  const plan = planen(aenderungen, (t, id) => aktuell.get(schluesselVon(t, id)), new Set(bytes.keys()), jetzt);
  await hinweiseBenennen(db, plan.hinweise);
  if (ganzOderGar && plan.bleibt.length) return { ...plan, puts: [], deletes: [], stehen: [], zurueck: 0 };

  const puts = new Map(plan.puts.map((p) => [schluesselVon(p.t, String(p.zeile.id)), p]));
  const weg = new Set(plan.deletes.map((d) => schluesselVon(d.t, d.id)));
  // Stehen gebliebene angelegte Zeilen auch pruefen: Ein neuer Zeitblock, der
  // inzwischen verschoben wurde, darf nicht weiter auf die Aufgabe zeigen,
  // wenn diese wieder ihren alten Block hat.
  const pruefen = [...plan.puts, ...plan.stehen.filter((s) => !puts.has(schluesselVon(s.t, String(s.zeile.id))))];
  const ziele = await lesen(db, verweisZiele(pruefen));
  const lies = (t: string, id: string) => {
    const k = schluesselVon(t, id);
    return weg.has(k) ? undefined : (puts.get(k)?.zeile ?? ziele.get(k));
  };
  for (const b of verweiseBereinigen(pruefen, lies, jetzt)) puts.set(schluesselVon(b.t, String(b.zeile.id)), b);

  const nachTabelle = new Map<string, Daten[]>();
  for (const { t, zeile } of puts.values()) {
    const id = String(zeile.id);
    const z = t === ANHAENGE && bytes.has(id) && zeile.bytes == null ? { ...zeile, bytes: bytes.get(id) } : zeile;
    nachTabelle.set(t, [...(nachTabelle.get(t) ?? []), z]);
  }
  for (const [t, zeilen] of nachTabelle) await db.table(t).bulkPut(zeilen);
  for (const d of plan.deletes) await db.table(d.t).delete(d.id);
  if (plan.zurueck) await applyLinkRule(db);
  return plan;
}

async function eintragEntfernen(db: OlePrivatDB, eintragId: string): Promise<void> {
  await db.papierkorb.delete(eintragId);
  await db.papierkorbZeilen.where('eintragId').equals(eintragId).delete();
  await db.papierkorbDateien.where('eintragId').equals(eintragId).delete();
}

/**
 * Den Rest neu schreiben - mit DERSELBEN Kopf-ID (sonst raeumt das Aufraeumen
 * die lokalen Dateien weg) und einem neuen Zeilen-Stand.
 */
async function restSchreiben(db: OlePrivatDB, kopf: PapierkorbEintrag, bleibt: Aenderung[], hinweise: Hinweis[], dateien: readonly PapierkorbDatei[], jetzt: string): Promise<void> {
  const stand = newId();
  const stuecke = zerlegen(bleibt);
  const offen = new Set(bleibt.map((a) => schluesselVon(a.t, a.id)));
  await db.papierkorbZeilen.where('eintragId').equals(kopf.id).delete();
  await db.papierkorbZeilen.bulkAdd(zeilenAus(kopf.id, stand, kopf.geloeschtAm, stuecke));
  await db.papierkorb.put({
    ...kopf, stand, zeilen: stuecke.length, zaehler: zaehlen(bleibt),
    hinweise: hinweise.filter((h) => offen.has(schluesselVon(h.t, h.id))), updatedAt: jetzt,
  });
  const behalten = new Set(bleibt.filter((a) => a.datei).map((a) => a.id));
  const ueberfluessig = dateien.filter((d) => !behalten.has(d.id)).map((d) => d.id);
  if (ueberfluessig.length) await db.papierkorbDateien.bulkDelete(ueberfluessig);
}

/** Zeilen des aktuellen Stands, in ihrer Reihenfolge. */
async function zeilenDesStands(db: OlePrivatDB, kopf: PapierkorbEintrag): Promise<PapierkorbZeile[]> {
  const alle = await db.papierkorbZeilen.where('eintragId').equals(kopf.id).toArray();
  return alle.filter((z) => z.stand === kopf.stand).sort((a, b) => a.nr - b.nr);
}

/**
 * Einen Papierkorb-Eintrag wiederherstellen. `null`: gibt es nicht (mehr);
 * `'unvollstaendig'`: noch nicht alle Zeilen per Sync angekommen. Was nicht
 * zurueck kann, bleibt als Rest im Papierkorb (mit Hinweisen).
 */
export async function wiederherstellen(db: OlePrivatDB, eintragId: string, jetzt = new Date()): Promise<WiederherstellErgebnis | 'unvollstaendig' | null> {
  return db.transaction('rw', db.tables, async (): Promise<WiederherstellErgebnis | 'unvollstaendig' | null> => {
    const kopf = await db.papierkorb.get(eintragId);
    if (!kopf) return null;
    const zeilen = await zeilenDesStands(db, kopf);
    if (!vollstaendig(kopf, zeilen.length)) return 'unvollstaendig';
    const dateien = await db.papierkorbDateien.where('eintragId').equals(eintragId).toArray();
    const zeit = jetzt.toISOString();
    const plan = await zurueckspielen(db, zeilen.flatMap((z) => z.aenderungen), dateien, zeit);
    if (plan.bleibt.length) await restSchreiben(db, kopf, plan.bleibt, plan.hinweise, dateien, zeit);
    else await eintragEntfernen(db, eintragId);
    return { titel: kopf.titel, zurueck: plan.zurueck, hinweise: plan.hinweise, rest: plan.bleibt.length };
  });
}

/**
 * Eine Aktion aus dem Speicher zuruecknehmen (Rueckgaengig ohne Papierkorb) -
 * ganz oder gar nicht: Kann etwas nicht zurueck, bleibt alles, wie es ist
 * (`zurueck: 0`, die Hinweise sagen warum).
 */
export async function zuruecknehmen(db: OlePrivatDB, aenderungen: readonly Aenderung[], jetzt = new Date()): Promise<{ zurueck: number; hinweise: Hinweis[] }> {
  return db.transaction('rw', db.tables, async () => {
    const plan = await zurueckspielen(db, aenderungen, [], jetzt.toISOString(), true);
    return { zurueck: plan.zurueck, hinweise: plan.hinweise };
  });
}

export async function endgueltigLoeschen(db: OlePrivatDB, eintragId: string): Promise<void> {
  await db.transaction('rw', db.papierkorbTabellen, () => eintragEntfernen(db, eintragId));
}

export async function papierkorbLeeren(db: OlePrivatDB): Promise<void> {
  await db.transaction('rw', db.papierkorbTabellen, async () => {
    for (const t of db.papierkorbTabellen) await t.clear();
  });
}

/**
 * Abgelaufenes endgueltig loeschen. Zeilen nur nach ihrem eigenen Datum -
 * ihr Kopf kann per Sync noch unterwegs sein. Lokale Dateien ohne Kopf
 * sind dagegen sicher verwaist (der Kopf entstand hier und ist weg).
 * Gibt die Zahl der entfernten Eintraege zurueck.
 */
export async function aufraeumen(db: OlePrivatDB, jetzt = new Date()): Promise<number> {
  const grenze = ablaufGrenze(jetzt);
  return db.transaction('rw', db.papierkorbTabellen, async () => {
    const alt = await db.papierkorb.where('geloeschtAm').below(grenze).primaryKeys();
    await db.papierkorb.bulkDelete(alt);
    await db.papierkorbZeilen.where('geloeschtAm').below(grenze).delete();
    const koepfe = await db.papierkorb.toCollection().primaryKeys();
    const verwaist = await db.papierkorbDateien.where('eintragId').noneOf(koepfe).primaryKeys();
    if (verwaist.length) await db.papierkorbDateien.bulkDelete(verwaist);
    return alt.length;
  });
}
